import { EventClickArg, EventHoveringArg } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import FullCalendar, { EventInput } from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import { addHours } from "date-fns";
import { Timestamp, where } from "firebase/firestore";
import { cloneDeep } from "lodash";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../Auth";
import MyChip from "../components/UI/Chip";
import { ProgressContainer } from "../components/UI/ProgressContainer";
import { DialogEditBooking } from "../dialogs/DialogEditBooking";
import { useHouseContext } from "../HouseContext";
import { getDisplayName, ReservationStatus, RoomReservation } from "../ostertalertypes";
import { fetchAllocationBookings} from "../utils/database";
import { handleError } from "../utils/utils";
import { getFirstDayOfPreviousMonth } from "../utils/dateutils";
import { hasAccessToHouse } from "../utils/adminutils";

const blue600 = "#2563eb" // bg-blue 600
const blue800 = "#1E40AF" // bg-blue 800
const red400 = "#f87171" // bg-red-400
const red600 = "#dc2626" // bg-red-600
const yellow400 =  "#fbbf24" //bg-amber-400
const yellow500 =  "#f59e0b" //bg-amber-500

function stripBrackets(text: string): string {
  return text.replace(/\s*\(.*?\)\s*/g, ' ').trim();
}

const statusToColor: { [key in ReservationStatus]: {normal:string, hover:string} } = {
  [ReservationStatus.ACCEPTED]: {normal: blue600, hover: blue800},
  [ReservationStatus.BOOKING_REQUESTED]: {normal: yellow400, hover: yellow500},
  [ReservationStatus.BOOKING_REJECTED]: {normal: blue600, hover: blue800},
  [ReservationStatus.CANCELED]: {normal: blue600, hover: blue800},
  [ReservationStatus.CANCEL_REQUESTED]: {normal: red400, hover: red600},
  [ReservationStatus.CANCEL_REJECTED]: {normal: blue600, hover: blue800},
};




const PageAllocationChecker: React.FC= () => {
  const { id } = useParams()
  const houseCtx = useHouseContext();
  const { enqueueSnackbar } = useSnackbar();

   // states for dialog
   const [selectedItem, setSelectedItem] = useState<RoomReservation | null>(null);
   const [isDialogEditOpen, setDialogEditOpen] = useState<boolean>(false);
   const [reload, setReload] = useState<boolean>(false);

   const fullCalendarRef = useRef<FullCalendar>(null);
   const auth = useAuthContext();

  // states for the reservations
  const [appReservations, setAppreservations] = useState<RoomReservation[]>([]);

  const house = houseCtx.houses.get(id!.toLowerCase());

  const [roomFilter, setRoomFilter] = useState<string[]>(Array.from(house?.rooms?.keys() || []));
  const [isLoading, setIsLoading] = useState<boolean>(false);


  useEffect(() => {
    async function fetchData() {
      try {
        if (!house) {
          return
        }
        setIsLoading(true)
        const calendarApi = fullCalendarRef.current?.getApi();
        if (!calendarApi) {
          return;
        }
        const curDate = calendarApi.getDate();
        const firstDayOfMonth =  getFirstDayOfPreviousMonth();
        const reservations = await fetchAllocationBookings(auth.isAdmin(),
            where("from", ">=", firstDayOfMonth),
            where("status", "in", [ReservationStatus.ACCEPTED, ReservationStatus.BOOKING_REQUESTED, ReservationStatus.CANCEL_REJECTED, ReservationStatus.CANCEL_REQUESTED]),
            where("houseName", "==", house.houseName));

        setAppreservations(reservations);
        setIsLoading(false);
      } catch (error) {
        handleError("Fehler beim Abrufen der Reservierungen", error, enqueueSnackbar, setIsLoading);
      }

    }
    fetchData()
  }, [reload, house, auth, enqueueSnackbar]);

  if (!house || !id) {
    return <>Loading...</>
  }



  function getEvents(): EventInput[] {

    function getEventTitle(res: RoomReservation): string {
      if (auth.isAdmin() && res.user) {
        return stripBrackets(res.roomName) + " " + getDisplayName(res.user);
      } else {
        return stripBrackets(res.roomName);
      }
    }

    let events: EventInput[] = [];
    // filter out all rooms which user has deselected
    if (appReservations.length === 0) {
      return events;
    }
    events = cloneDeep(appReservations)
      .filter((reservation) => roomFilter.includes(reservation.roomName))
      .map((reservation) => {
        return {
          id: reservation.id,
          start: reservation.from?.toDate(),
          end: reservation.to && addHours(reservation.to.toDate(), 12), // add 12 hours so that leaving day is also highlighted in calendar
          title: getEventTitle(reservation),
          color: statusToColor[reservation.status].normal,
          extendedProps: { status: reservation.status}
        } as EventInput;
      });
    return events;
  }

  function filterHandler(addedFilter: string) {
    let newfilter: string[];
    // room does not exit => add it
    if (roomFilter.indexOf(addedFilter) < 0) {
      newfilter = roomFilter.concat(addedFilter);
    } else {
      // room exists => remove it
      newfilter = roomFilter.filter((val) => val !== addedFilter);
    }
    setRoomFilter(newfilter);
  }

  function onMouseEnter(mouseEnterInfo:EventHoveringArg) {
    const status = mouseEnterInfo.event.extendedProps["status"] as ReservationStatus;
    mouseEnterInfo.event.setProp("color", statusToColor[status].hover);
  }

  function onMouseLeave(mouseEnterInfo:EventHoveringArg) {
    const status = mouseEnterInfo.event.extendedProps["status"] as ReservationStatus;
    mouseEnterInfo.event.setProp("color", statusToColor[status].normal);
  }

  function onEventClick(eventClickArg:EventClickArg) {
    if (auth.isAdmin()) {
      const id = eventClickArg.event.id;
      let res = appReservations.filter(res => res.id === id);
      if (res.length > 0) {
        // Check if admin has access to this house
        if (hasAccessToHouse(auth.adminHouses, res[0].houseName)) {
          setSelectedItem(cloneDeep(res[0]));
          setDialogEditOpen(true);
        }
      }
    }
  }

  const onEditDialogChanged = async (appartment: RoomReservation, index: number) => {
    setSelectedItem(cloneDeep(appartment));
  };

  const onGuestCountChanged = (guestCount: number) => {
    if (selectedItem) {
      setSelectedItem({...selectedItem, guestCount: guestCount});
    }
  };

  const onEditDialogClose = (changed: boolean) => {
    setDialogEditOpen(false);
    setSelectedItem(null);
    if (changed) {
      setReload((oldVal) => !oldVal);
    }
  };

  return (
    <ProgressContainer size="max-w-5xl" isLoading={isLoading}>
      <div className="md:mx-10">
        <h1 className="text-3xl mt-0 mb-10 text-gray-500 text-center md:text-left">Belegungsplan {house.houseName}</h1>
        <div>
          <div className="flex flex-wrap mb-5">
            {Array.from(house.rooms.values()).map((room) => {
              return <MyChip key={room.roomName} text={stripBrackets(room.roomName)} onClick={filterHandler.bind(null, room.roomName)}></MyChip>;
            })}
          </div>
          <FullCalendar
            height={"auto"}
            ref={fullCalendarRef}
            plugins={[dayGridPlugin]}
            editable={auth.isAdmin()? true : false}
            displayEventTime={false}
            selectable={false}
            weekends={true}
            locales={[deLocale]}
            locale="de"
            eventClick={onEventClick}
            eventMouseEnter={onMouseEnter}
            eventMouseLeave={onMouseLeave}
            initialView="dayGridMonth"
            events={getEvents()}
          />
        </div>
      </div>
      {selectedItem != null && isDialogEditOpen && (
        <DialogEditBooking
          house={houseCtx.houses.get(selectedItem.houseName.toLowerCase())!}
          open={isDialogEditOpen}
          allowCancel={auth.isAdmin() ? true : undefined}
          onClose={onEditDialogClose}
          onBookingChanged={onEditDialogChanged}
          onGuestCountChanged={onGuestCountChanged}
          roomReservation={selectedItem}
        />
      )}
    </ProgressContainer>

  );
};

export default PageAllocationChecker;
